import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Treemap, Sankey
} from 'recharts';
import { 
  Activity, TrendingUp, TrendingDown, Users, DollarSign,
  Heart, Shield, AlertTriangle, RefreshCw, Loader2,
  Brain, Sparkles, Target, Zap, Clock, ArrowRight,
  CheckCircle2, ArrowUpRight, ArrowDownRight, Minus,
  Building, Gauge, Lightbulb, ChevronRight, GitBranch,
  Download, Play, Pause, RotateCcw, Layers, Network,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { 
  PageHeader, MetricCard, ModernCard, SectionHeader, StatusBadge,
  modernTabsClass, modernTabTriggerClass, GradientProgress, RiskGauge
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status color mapping
const statusColors = {
  excellent: 'bg-emerald-500',
  good: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500'
};

const statusTextColors = {
  excellent: 'text-emerald-600',
  good: 'text-blue-600',
  warning: 'text-amber-600',
  critical: 'text-red-600'
};

// Cascade Flow Visualization Component
const CascadeFlow = ({ data }) => {
  const domains = [
    { key: 'census', name: 'Census', icon: Building, color: 'from-amber-500 to-orange-600' },
    { key: 'workforce', name: 'Workforce', icon: Users, color: 'from-blue-500 to-indigo-600' },
    { key: 'financial', name: 'Financial', icon: DollarSign, color: 'from-emerald-500 to-teal-600' },
    { key: 'clinical', name: 'Clinical', icon: Heart, color: 'from-rose-500 to-pink-600' },
    { key: 'compliance', name: 'Compliance', icon: Shield, color: 'from-violet-500 to-purple-600' }
  ];

  return (
    <div className="relative">
      {/* Flow line */}
      <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-blue-500 via-emerald-500 via-rose-500 to-violet-500 opacity-30 -translate-y-1/2" />
      
      <div className="relative grid grid-cols-5 gap-4">
        {domains.map((domain, idx) => {
          const Icon = domain.icon;
          const domainData = data?.[domain.key] || {};
          const score = data?.executive_health_score?.breakdown?.[domain.key]?.score || 0;
          
          return (
            <div key={domain.key} className="relative" data-testid={`cascade-${domain.key}`}>
              {/* Arrow between nodes */}
              {idx < domains.length - 1 && (
                <ArrowRight className="absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
              )}
              
              <Card className={`hover:shadow-lg transition-all cursor-pointer group`}>
                <CardContent className="p-4 text-center">
                  <div className={`mx-auto w-12 h-12 rounded-xl bg-gradient-to-br ${domain.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-semibold text-sm">{domain.name}</h4>
                  <div className="mt-2">
                    <span className="text-2xl font-bold">{score.toFixed(0)}</span>
                    <span className="text-xs text-muted-foreground">/100</span>
                  </div>
                  <Progress value={score} className="h-1.5 mt-2" />
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Executive Health Score Gauge
const HealthScoreGauge = ({ score, status, statusColor }) => {
  const circumference = 2 * Math.PI * 80;
  const strokeDasharray = `${(score / 100) * circumference} ${circumference}`;
  
  const getColor = (s) => {
    if (s >= 85) return '#10B981';
    if (s >= 70) return '#3B82F6';
    if (s >= 55) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="relative flex flex-col items-center justify-center" data-testid="health-score-gauge">
      <svg className="transform -rotate-90" width="200" height="200">
        <circle
          cx="100"
          cy="100"
          r="80"
          stroke="#e5e7eb"
          strokeWidth="16"
          fill="none"
        />
        <circle
          cx="100"
          cy="100"
          r="80"
          stroke={getColor(score)}
          strokeWidth="16"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-5xl font-bold" style={{ color: getColor(score) }}>
          {score.toFixed(0)}
        </span>
        <span className="text-sm text-muted-foreground mt-1">Health Score</span>
        <Badge 
          className="mt-2"
          style={{ backgroundColor: getColor(score) + '20', color: getColor(score) }}
        >
          {status}
        </Badge>
      </div>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const typeConfig = {
    opportunity: { icon: Sparkles, gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    warning: { icon: AlertTriangle, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    risk: { icon: AlertCircle, gradient: 'from-red-500 to-rose-600', bg: 'bg-red-50 dark:bg-red-900/20' },
    action: { icon: Target, gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50 dark:bg-blue-900/20' }
  };

  const config = typeConfig[insight.type] || typeConfig.action;
  const Icon = config.icon;

  return (
    <Card className={`${config.bg} border-l-4 border-l-current`} style={{ borderLeftColor: insight.type === 'risk' ? '#EF4444' : insight.type === 'warning' ? '#F59E0B' : insight.type === 'opportunity' ? '#10B981' : '#3B82F6' }}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${config.gradient}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">{insight.title}</h4>
              <Badge variant="outline" className="text-xs">{insight.domain}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
            <Badge variant="secondary" className="mt-2 text-xs">
              Impact: {insight.impact}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recommendation Card Component
const RecommendationCard = ({ rec, index }) => {
  const priorityColors = {
    HIGH: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    LOW: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
  };

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`recommendation-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <Badge className={priorityColors[rec.priority]}>{rec.priority}</Badge>
          <Badge variant="outline" className="text-xs">{rec.category}</Badge>
        </div>
        <h4 className="font-semibold text-sm mt-2">{rec.action}</h4>
        <p className="text-sm text-muted-foreground mt-1">{rec.rationale}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-xs text-emerald-600 font-medium">{rec.impact}</span>
          <span className="text-xs text-muted-foreground">{rec.timeline}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// What-If Simulation Panel
const SimulationPanel = ({ onSimulate, loading }) => {
  const [adcChange, setAdcChange] = useState(0);
  const [admissionsChange, setAdmissionsChange] = useState(0);
  const [rncmChange, setRncmChange] = useState(0);

  const handleSimulate = () => {
    onSimulate({
      scenario_name: "Custom Simulation",
      adc_change: adcChange,
      admissions_change: admissionsChange,
      rncm_change: rncmChange
    });
  };

  const handleReset = () => {
    setAdcChange(0);
    setAdmissionsChange(0);
    setRncmChange(0);
  };

  return (
    <Card data-testid="simulation-panel">
      <CardHeader>
        <SectionHeader 
          icon={GitBranch} 
          title="What-If Simulation" 
          subtitle="See how changes cascade through all metrics"
        />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">ADC Change</Label>
              <span className="text-sm font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                {adcChange >= 0 ? '+' : ''}{adcChange}
              </span>
            </div>
            <Slider
              value={[adcChange]}
              onValueChange={([v]) => setAdcChange(v)}
              min={-20}
              max={20}
              step={1}
              className="w-full"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Weekly Admissions Change</Label>
              <span className="text-sm font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                {admissionsChange >= 0 ? '+' : ''}{admissionsChange}
              </span>
            </div>
            <Slider
              value={[admissionsChange]}
              onValueChange={([v]) => setAdmissionsChange(v)}
              min={-10}
              max={10}
              step={1}
              className="w-full"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">RNCM Staff Change</Label>
              <span className="text-sm font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                {rncmChange >= 0 ? '+' : ''}{rncmChange}
              </span>
            </div>
            <Slider
              value={[rncmChange]}
              onValueChange={([v]) => setRncmChange(v)}
              min={-3}
              max={5}
              step={1}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleSimulate} 
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Run Simulation
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Simulation Results Component
const SimulationResults = ({ results }) => {
  if (!results) return null;

  const { baseline, simulated, delta, cascade_effects } = results;

  return (
    <Card className="border-[#00b0ab]/30 dark:border-indigo-800" data-testid="simulation-results">
      <CardHeader>
        <SectionHeader 
          icon={Network} 
          title="Cascade Effects" 
          subtitle="How your changes ripple through operations"
        />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Delta Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'ADC', baseline: baseline.adc, sim: simulated.adc, delta: delta.adc },
            { label: 'Health Score', baseline: baseline.health_score, sim: simulated.health_score, delta: delta.health_score },
            { label: 'Utilization', baseline: baseline.utilization, sim: simulated.utilization, delta: delta.utilization, suffix: '%' },
            { label: 'Revenue', baseline: baseline.revenue, sim: simulated.revenue, delta: delta.revenue, prefix: '$', suffix: 'K' }
          ].map((metric, idx) => (
            <Card key={idx} className="bg-slate-50 dark:bg-slate-800/50">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">{metric.label}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">
                    {metric.prefix}{metric.baseline?.toFixed(1)}{metric.suffix}
                  </span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="text-lg font-bold">
                    {metric.prefix}{metric.sim?.toFixed(1)}{metric.suffix}
                  </span>
                </div>
                <Badge 
                  className={`mt-1 text-xs ${metric.delta >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                >
                  {metric.delta >= 0 ? '+' : ''}{metric.delta?.toFixed(1)}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cascade Effects List */}
        {cascade_effects && cascade_effects.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Cascade Chain:</h4>
            {cascade_effects.map((effect, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <Badge variant="outline">{effect.source}</Badge>
                <span className="text-sm">{effect.metric}</span>
                <Badge className={effect.change.startsWith('+') || effect.change.startsWith('$+') ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {effect.change}
                </Badge>
                <span className="text-xs text-muted-foreground ml-auto">{effect.impact}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
export default function IntegratedKPICenter() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Fetch integrated dashboard data
  const fetchDashboardData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/integrated-operations/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
        if (isRefresh) toast.success('Dashboard refreshed');
      } else {
        toast.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Run what-if simulation
  const runSimulation = async (params) => {
    setSimulationLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/integrated-operations/simulate`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      
      if (res.ok) {
        const data = await res.json();
        setSimulationResults(data);
        toast.success('Simulation complete');
      }
    } catch (error) {
      console.error('Error running simulation:', error);
      toast.error('Simulation failed');
    } finally {
      setSimulationLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  // Auto-refresh effect
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => fetchDashboardData(false), 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, token]);

  // Forecast chart data
  const forecastData = useMemo(() => {
    if (!dashboardData?.forecasts?.weekly_projections) return [];
    return [
      { week: 'Current', ...dashboardData.census, revenue: dashboardData.financial?.weekly_revenue / 1000 },
      ...dashboardData.forecasts.weekly_projections.map(f => ({
        week: `Week ${f.week}`,
        adc: f.adc,
        revenue: f.revenue,
        utilization: f.utilization
      }))
    ];
  }, [dashboardData]);

  // Strategic alignment data
  const strategicData = useMemo(() => {
    if (!dashboardData?.strategic_alignment) return null;
    const sa = dashboardData.strategic_alignment;
    return {
      current: sa.current_adc,
      target: sa.target_adc,
      progress: sa.overall_progress,
      nextMilestone: sa.next_milestone
    };
  }, [dashboardData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="kpi-center-loading">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  const healthScore = dashboardData?.executive_health_score || { score: 0, status: 'Unknown' };
  const anchorMetric = dashboardData?.anchor_metric || { value: 0, status: 'unknown' };

  return (
    <div className="space-y-6" data-testid="integrated-kpi-center">
      {/* Header */}
      <PageHeader
        icon={Network}
        title="Integrated Operations AI Agent"
        subtitle="Real-time cascading metrics intelligence engine"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1.5">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              className="data-[state=checked]:bg-white"
            />
            <Label htmlFor="auto-refresh" className="text-white text-sm cursor-pointer">
              Auto-refresh
            </Label>
          </div>
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Anchor Metric + Health Score Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Anchor Metric Card */}
        <ModernCard className="lg:col-span-1">
          <CardHeader>
            <SectionHeader 
              icon={Target} 
              title="Anchor Metric" 
              subtitle="Primary driver of all operations"
            />
          </CardHeader>
          <CardContent className="text-center pb-6">
            <div className="text-6xl font-bold text-[#00b0ab] dark:text-[#33c3bf]">
              {anchorMetric.value}
            </div>
            <p className="text-lg text-muted-foreground mt-1">Average Daily Census</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Badge className={statusColors[anchorMetric.status]}>{anchorMetric.status}</Badge>
              <Badge variant="outline">{anchorMetric.growth_status}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-4 italic">
              "All metrics cascade from ADC"
            </p>
          </CardContent>
        </ModernCard>

        {/* Executive Health Score */}
        <ModernCard className="lg:col-span-1">
          <CardHeader className="pb-0">
            <SectionHeader 
              icon={Gauge} 
              title="Executive Health" 
              subtitle="Weighted operational score"
            />
          </CardHeader>
          <CardContent className="flex justify-center py-4">
            <HealthScoreGauge 
              score={healthScore.score} 
              status={healthScore.status}
              statusColor={healthScore.status_color}
            />
          </CardContent>
        </ModernCard>

        {/* Strategic Alignment */}
        <ModernCard className="lg:col-span-1">
          <CardHeader>
            <SectionHeader 
              icon={TrendingUp} 
              title="Strategic Alignment" 
              subtitle="5-Year Plan Progress (170→450 ADC)"
            />
          </CardHeader>
          <CardContent>
            {strategicData && (
              <div className="space-y-4">
                <div className="text-center">
                  <span className="text-4xl font-bold text-emerald-600">{strategicData.progress.toFixed(1)}%</span>
                  <p className="text-sm text-muted-foreground">of target achieved</p>
                </div>
                <Progress value={strategicData.progress} className="h-3" />
                <div className="flex justify-between text-sm">
                  <span>Current: {strategicData.current}</span>
                  <span>Target: {strategicData.target}</span>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Next Milestone</p>
                  <p className="text-2xl font-bold">{strategicData.nextMilestone} ADC</p>
                </div>
              </div>
            )}
          </CardContent>
        </ModernCard>
      </div>

      {/* Cascade Flow Visualization */}
      <ModernCard>
        <CardHeader>
          <SectionHeader 
            icon={GitBranch} 
            title="Cascading Metrics Flow" 
            subtitle="Input → Analysis → Cross-Module Update → Forecast → Insight → Recommendation"
          />
        </CardHeader>
        <CardContent>
          <CascadeFlow data={dashboardData} />
        </CardContent>
      </ModernCard>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <Layers className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="insights" className={modernTabTriggerClass}>
            <Sparkles className="h-4 w-4 mr-2" /> Insights
          </TabsTrigger>
          <TabsTrigger value="forecasts" className={modernTabTriggerClass}>
            <TrendingUp className="h-4 w-4 mr-2" /> Forecasts
          </TabsTrigger>
          <TabsTrigger value="simulation" className={modernTabTriggerClass}>
            <GitBranch className="h-4 w-4 mr-2" /> What-If
          </TabsTrigger>
          <TabsTrigger value="recommendations" className={modernTabTriggerClass}>
            <Lightbulb className="h-4 w-4 mr-2" /> Actions
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Census Metrics */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Building} title="Census & Growth" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Weekly Admissions</p>
                    <p className="text-2xl font-bold">{dashboardData?.census?.weekly_admissions || 0}</p>
                    <Badge variant="outline" className="mt-1">
                      Target: 20/week
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Net Growth</p>
                    <p className={`text-2xl font-bold ${(dashboardData?.census?.net_growth || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {dashboardData?.census?.net_growth >= 0 ? '+' : ''}{dashboardData?.census?.net_growth || 0}
                    </p>
                    <Badge className={dashboardData?.census?.is_true_growth ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {dashboardData?.census?.is_true_growth ? 'True Growth' : 'Replacement'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Discharge Rate</p>
                    <p className="text-2xl font-bold">{dashboardData?.census?.discharge_rate_pct || 0}%</p>
                    <Badge variant="outline">Target: 7%</Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Referral Conversion</p>
                    <p className="text-2xl font-bold">{((dashboardData?.census?.referral_conversion || 0) * 100).toFixed(0)}%</p>
                    <Badge variant="outline">Target: 75%</Badge>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* Workforce Metrics */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Users} title="Workforce & Capacity" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Staff Utilization</p>
                    <p className="text-2xl font-bold">{dashboardData?.workforce?.staff_utilization || 0}%</p>
                    <Badge className={statusColors[dashboardData?.workforce?.utilization_status || 'warning']}>
                      {dashboardData?.workforce?.utilization_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Avg Caseload</p>
                    <p className="text-2xl font-bold">{dashboardData?.workforce?.avg_caseload || 0}</p>
                    <Badge variant="outline">Max: 15</Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Burnout Risk</p>
                    <p className="text-2xl font-bold">{dashboardData?.workforce?.burnout_critical_count || 0}</p>
                    <Badge className={statusColors[dashboardData?.workforce?.burnout_status || 'warning']}>
                      {dashboardData?.workforce?.burnout_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">FTE Gap</p>
                    <p className={`text-2xl font-bold ${(dashboardData?.workforce?.fte_gap || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {dashboardData?.workforce?.fte_gap || 0}
                    </p>
                    <Badge variant="outline">Need: {dashboardData?.workforce?.required_rncms || 0} RNCMs</Badge>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* Financial Metrics */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={DollarSign} title="Financial Health" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Weekly Revenue</p>
                    <p className="text-2xl font-bold">${((dashboardData?.financial?.weekly_revenue || 0) / 1000).toFixed(0)}K</p>
                    <Badge className={statusColors[dashboardData?.financial?.revenue_status || 'warning']}>
                      {dashboardData?.financial?.revenue_vs_target_pct || 0}% of target
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Net Margin</p>
                    <p className="text-2xl font-bold">{dashboardData?.financial?.net_margin_pct || 0}%</p>
                    <Badge className={statusColors[dashboardData?.financial?.margin_status || 'warning']}>
                      {dashboardData?.financial?.margin_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Cost per Episode</p>
                    <p className="text-2xl font-bold">${((dashboardData?.financial?.cost_per_episode || 0) / 1000).toFixed(1)}K</p>
                    <Badge variant="outline">Target: $23K</Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">PRN Utilization</p>
                    <p className="text-2xl font-bold">{dashboardData?.workforce?.prn_utilization || 0}%</p>
                    <Badge variant="outline">Target: &lt;12%</Badge>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* Clinical & Compliance */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Heart} title="Clinical & Compliance" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Pain Control (48h)</p>
                    <p className="text-2xl font-bold">{dashboardData?.clinical?.pain_control_48h || 0}%</p>
                    <Badge className={statusColors[dashboardData?.clinical?.pain_control_status || 'good']}>
                      {dashboardData?.clinical?.pain_control_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Hospitalization Rate</p>
                    <p className="text-2xl font-bold">{dashboardData?.clinical?.hospitalization_rate || 0}%</p>
                    <Badge className={statusColors[dashboardData?.clinical?.hospitalization_status || 'good']}>
                      {dashboardData?.clinical?.hospitalization_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Overall Compliance</p>
                    <p className="text-2xl font-bold">{dashboardData?.compliance?.overall_compliance || 0}%</p>
                    <Badge className={statusColors[dashboardData?.compliance?.overall_status || 'good']}>
                      {dashboardData?.compliance?.overall_status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">CHAP Score</p>
                    <p className="text-2xl font-bold">{dashboardData?.compliance?.chap_score || 0}%</p>
                    <Badge className={statusColors[dashboardData?.compliance?.chap_status || 'good']}>
                      {dashboardData?.compliance?.chap_status || 'N/A'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights">
          <ModernCard>
            <CardHeader>
              <SectionHeader 
                icon={Sparkles} 
                title="AI-Generated Executive Insights" 
                subtitle="Derived from cascading analysis across all domains"
              />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(dashboardData?.insights || []).map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
                {(!dashboardData?.insights || dashboardData.insights.length === 0) && (
                  <div className="col-span-2 text-center py-8 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No insights available at this time</p>
                  </div>
                )}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Forecasts Tab */}
        <TabsContent value="forecasts" className="space-y-6">
          <ModernCard>
            <CardHeader>
              <SectionHeader 
                icon={TrendingUp} 
                title="4-Week Projections" 
                subtitle={`Confidence: ${dashboardData?.forecasts?.confidence_level || 0}%`}
              />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={forecastData}>
                  <defs>
                    <linearGradient id="colorAdc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="week" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }} 
                  />
                  <Legend />
                  <Area 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="adc" 
                    name="Projected ADC" 
                    stroke="#6366F1" 
                    fill="url(#colorAdc)" 
                    strokeWidth={2}
                  />
                  <Area 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="revenue" 
                    name="Revenue ($K)" 
                    stroke="#10B981" 
                    fill="url(#colorRev)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </ModernCard>

          {/* Risk Factors */}
          {dashboardData?.forecasts?.risk_factors && (
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={AlertTriangle} title="Forecast Risk Factors" />
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {dashboardData.forecasts.risk_factors.map((risk, idx) => (
                    <li key={idx} className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm">{risk}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Simulation Tab */}
        <TabsContent value="simulation" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SimulationPanel onSimulate={runSimulation} loading={simulationLoading} />
            <SimulationResults results={simulationResults} />
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <ModernCard>
            <CardHeader>
              <SectionHeader 
                icon={Lightbulb} 
                title="Operational Recommendations" 
                subtitle="Actionable items based on cascading analysis"
              />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(dashboardData?.recommendations || []).map((rec, idx) => (
                  <RecommendationCard key={idx} rec={rec} index={idx} />
                ))}
                {(!dashboardData?.recommendations || dashboardData.recommendations.length === 0) && (
                  <div className="col-span-2 text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                    <p>All metrics are on target - no urgent recommendations</p>
                  </div>
                )}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>

      {/* Cascade Log (for transparency) */}
      {dashboardData?.cascade_log && dashboardData.cascade_log.length > 0 && (
        <ModernCard>
          <CardHeader>
            <SectionHeader 
              icon={Activity} 
              title="Cascade Calculation Log" 
              subtitle="Transparency into metric flow"
            />
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-40">
              <div className="space-y-1">
                {dashboardData.cascade_log.slice(-10).map((log, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-xs p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                    <Badge variant="outline" className="text-xs">{log.module}</Badge>
                    <span className="text-muted-foreground">{log.message}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </ModernCard>
      )}
    </div>
  );
}
